import React, { useState, useEffect } from 'react';
import { clickable } from '../../../utils/a11y';
import { Link, useNavigate } from 'react-router-dom';
import './CloudStaffDashboard.css';
import cloudsafeLogo from '../../../assets/cloudsafe-logo.png';
import {
  FaDatabase, FaHistory, FaBuilding, FaEdit, FaSignOutAlt, FaUserCircle,
  FaBell, FaChartBar, FaClipboardCheck, FaExternalLinkAlt, FaCode, FaServer,
  FaTable, FaHeartbeat, FaShieldAlt, FaToggleOn, FaChartPie, FaTools,
  FaExclamationTriangle, FaCheckCircle, FaTimesCircle, FaBars, FaArrowLeft
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getUserRole } from '../../../utils/roleUtils';
import { clearToken } from '../../../utils/auth';
import { getApiEndpoint } from '../../../config/api';
import CloudStaffSidebar from './CloudStaffSidebar';
import UserRoleBadge from '../../shared/UserRoleBadge';
import '../../shared/shared.css';

const CloudStaffDashboard = () => {
  const navigate = useNavigate();
  const [userName, setUserName] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [stats, setStats] = useState({
    totalProperties: 0, assetsModifiedToday: 0, totalAssetsModified: 0, propertiesModified: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [integrityData, setIntegrityData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userRole = getUserRole();
    if (userRole !== 'cloud_staff' && userRole !== 'super_admin') {
      toast.error('Access denied');
      navigate('/login');
      return;
    }
    fetchDashboardData();
  }, [navigate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };

      const [profileRes, dashRes, integrityRes] = await Promise.all([
        fetch(getApiEndpoint('/api/users/profile'), { headers }),
        fetch(getApiEndpoint('/api/cloud-staff/dashboard'), { headers }),
        fetch(getApiEndpoint('/api/cloud-staff/data-integrity'), { headers })
      ]);

      if (profileRes.ok) {
        const d = await profileRes.json();
        setUserName(`${d.firstName || d.first_name || ''} ${d.lastName || d.last_name || ''}`);
      }
      if (dashRes.ok) {
        const d = await dashRes.json();
        if (d.stats) setStats(d.stats);
        if (d.recentActivity) setRecentActivity(d.recentActivity);
      }
      if (integrityRes.ok) {
        setIntegrityData(await integrityRes.json());
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    clearToken();
    localStorage.removeItem('userRole');
    localStorage.removeItem('token');
    navigate('/login');
    toast.success('Logged out successfully');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="cs-dashboard">
      {/* Header */}
      <header className="cs-header">
        <div className="cs-header-left">
          <button className="cs-mobile-menu-btn" onClick={() => setMobileSidebarOpen(true)} aria-label="Open menu">
            <FaBars />
          </button>
          <img src={cloudsafeLogo} alt="CloudSafe" className="cs-logo" />
          <div className="cs-header-badge"><FaServer /><span>Cloud Staff Portal</span></div>
        </div>
        <div className="cs-header-right">
          <Link to="/account-settings" className="cs-back-to-account-btn" title="Back to account settings">
            <FaArrowLeft />
            <span>Back to account settings</span>
          </Link>
          <div className="cs-header-icons">
            <UserRoleBadge />
            <button className="cs-icon-btn" title="Notifications"><FaBell /></button>
            <div className="cs-user-avatar" aria-haspopup="menu" aria-expanded={showUserMenu}
                 {...clickable(() => setShowUserMenu(!showUserMenu), { label: 'User menu' })}>
              <FaUserCircle />
              {showUserMenu && (
                <div className="cs-user-menu">
                  <div className="cs-user-menu-header">
                    <FaUserCircle className="cs-menu-avatar" />
                    <div>
                      <p className="cs-menu-name">{userName || 'Cloud Staff'}</p>
                      <p className="cs-menu-role">Cloud Staff Admin</p>
                    </div>
                  </div>
                  <div className="cs-user-menu-divider" />
                  <Link to="/cloud-staff/activity-log" className="cs-menu-item"><FaHistory /> Activity Log</Link>
                  <button onClick={handleLogout} className="cs-menu-item cs-menu-logout"><FaSignOutAlt /> Sign Out</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="cs-layout">
        <CloudStaffSidebar
          active="overview"
          mobileOpen={mobileSidebarOpen}
          onClose={() => setMobileSidebarOpen(false)}
        />

        <main className="cs-content">
          {loading ? (
            <div className="cs-loading"><div className="cs-spinner" /><p>Loading dashboard...</p></div>
          ) : (
            <>
              {/* Welcome */}
              <div className="cs-welcome">
                <div>
                  <h1>Welcome back, {userName || 'Staff'}</h1>
                  <p>Cloud Staff Administration Portal</p>
                </div>
                <Link to="/cloud-staff/bulk-editor" className="cs-primary-btn"><FaTable /> Open Bulk Editor</Link>
              </div>

              {/* Stats */}
              <div className="cs-stats-grid">
                <div className="cs-stat-card">
                  <div className="cs-stat-icon buildings"><FaBuilding /></div>
                  <div className="cs-stat-info">
                    <span className="cs-stat-value">{stats.totalProperties}</span>
                    <span className="cs-stat-label">Total Buildings</span>
                  </div>
                </div>
                <div className="cs-stat-card">
                  <div className="cs-stat-icon today"><FaEdit /></div>
                  <div className="cs-stat-info">
                    <span className="cs-stat-value">{stats.assetsModifiedToday}</span>
                    <span className="cs-stat-label">Modified Today</span>
                  </div>
                </div>
                <div className="cs-stat-card">
                  <div className="cs-stat-icon total"><FaDatabase /></div>
                  <div className="cs-stat-info">
                    <span className="cs-stat-value">{stats.totalAssetsModified}</span>
                    <span className="cs-stat-label">Total Modified</span>
                  </div>
                </div>
                <div className="cs-stat-card">
                  <div className="cs-stat-icon properties"><FaClipboardCheck /></div>
                  <div className="cs-stat-info">
                    <span className="cs-stat-value">{stats.propertiesModified}</span>
                    <span className="cs-stat-label">Properties Touched</span>
                  </div>
                </div>
              </div>

              {/* Data Integrity Summary */}
              {integrityData && integrityData.checks.length > 0 && (
                <div className="cs-section">
                  <div className="cs-section-header">
                    <h2 className="cs-section-title"><FaShieldAlt style={{ marginRight: '0.5rem', color: '#ffa657' }} /> Data Integrity Alerts</h2>
                    <Link to="/cloud-staff/data-integrity" className="cs-view-all">View All <FaExternalLinkAlt /></Link>
                  </div>
                  <div className="cs-integrity-summary">
                    {integrityData.summary.critical > 0 && (
                      <div className="cs-integrity-badge critical">
                        <FaTimesCircle /> {integrityData.summary.critical} Critical
                      </div>
                    )}
                    {integrityData.summary.warnings > 0 && (
                      <div className="cs-integrity-badge warning">
                        <FaExclamationTriangle /> {integrityData.summary.warnings} Warnings
                      </div>
                    )}
                    {integrityData.checks.length === 0 && (
                      <div className="cs-integrity-badge ok">
                        <FaCheckCircle /> All Clear
                      </div>
                    )}
                  </div>
                  <div className="cs-integrity-list">
                    {integrityData.checks.slice(0, 5).map((check, i) => (
                      <div key={i} className={`cs-integrity-item ${check.severity}`}>
                        {check.severity === 'critical' ? <FaTimesCircle /> : <FaExclamationTriangle />}
                        <span>{check.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div className="cs-section">
                <h2 className="cs-section-title">Tools</h2>
                <div className="cs-actions-grid cs-actions-grid-4">
                  <Link to="/cloud-staff/bulk-editor" className="cs-action-card">
                    <div className="cs-action-icon code"><FaTable /></div>
                    <h3>Bulk Asset Editor</h3>
                    <p>Spreadsheet-style editing</p>
                  </Link>
                  <Link to="/cloud-staff/asset-code-tool" className="cs-action-card">
                    <div className="cs-action-icon history"><FaCode /></div>
                    <h3>Asset Code Tool</h3>
                    <p>Reference ID manager</p>
                  </Link>
                  <Link to="/cloud-staff/system-health" className="cs-action-card">
                    <div className="cs-action-icon health"><FaHeartbeat /></div>
                    <h3>System Health</h3>
                    <p>Monitor uptime & errors</p>
                  </Link>
                  <Link to="/cloud-staff/metrics" className="cs-action-card">
                    <div className="cs-action-icon metrics"><FaChartPie /></div>
                    <h3>SaaS Metrics</h3>
                    <p>Usage & business data</p>
                  </Link>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="cs-section">
                <div className="cs-section-header">
                  <h2 className="cs-section-title">Recent Activity</h2>
                  <Link to="/cloud-staff/activity-log" className="cs-view-all">View All <FaExternalLinkAlt /></Link>
                </div>
                {recentActivity.length > 0 ? (
                  <div className="cs-activity-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Time</th><th>Building</th><th>Module</th><th>Asset</th><th>Field</th><th>Old</th><th>New</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentActivity.slice(0, 10).map((a) => (
                          <tr key={a.id}>
                            <td>{formatDate(a.created_at)}</td>
                            <td>{a.property_name || `#${a.property_id}`}</td>
                            <td><span className="cs-badge module">{a.safety_module}</span></td>
                            <td>{a.asset_name || `#${a.asset_id}`}</td>
                            <td>{a.field_changed}</td>
                            <td className="cs-old-value">{a.old_value || '(empty)'}</td>
                            <td className="cs-new-value">{a.new_value || '(empty)'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="cs-empty-state">
                    <FaDatabase />
                    <p>No activity yet. Use the Bulk Editor to start managing assets.</p>
                    <Link to="/cloud-staff/bulk-editor" className="cs-primary-btn"><FaTable /> Get Started</Link>
                  </div>
                )}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default CloudStaffDashboard;
